import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import service from '../api/ApiRoutes';

const initialState = {
  salaries: [],
  salary: null,
  totalSalaries: 0,
  totalPages: 1,
  currentPage: 1,
  status: 'idle',
  error: null,
  successMessage: '',
  searchQuery: '',
};

// Create a new salary
export const createSalary = createAsyncThunk(
  'salaries/createSalary',
  async (salaryData, thunkAPI) => {
    try {
      const response = await service.createSalary(salaryData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Fetch all salaries with search and pagination
export const fetchSalaries = createAsyncThunk(
  'salaries/fetchSalaries',
  async ({ page, searchQuery }, thunkAPI) => {
    try {
      const response = await service.getSalaries({ page, searchTerm: searchQuery });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Fetch all salaries
export const fetchAllSalaries = createAsyncThunk(
  'salaries/fetchAllSalaries',
  async (config, thunkAPI) => {
    try {
      const response = await service.getAllSalaries(config);
      return response.data;
    } catch (error) {
      console.error('API error:', error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Fetch a single salary by ID
export const fetchSalaryById = createAsyncThunk(
  'salaries/fetchSalaryById',
  async (salaryId, thunkAPI) => {
    try {
      const response = await service.getSalaryById(salaryId);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Update a salary
export const updateSalary = createAsyncThunk(
  'salaries/updateSalary',
  async ({ id, salaryData, config }, thunkAPI) => {
    try {
      const response = await service.updateSalary(id, salaryData, config);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Delete a salary
export const deleteSalary = createAsyncThunk(
  'salaries/deleteSalary',
  async (salaryId, thunkAPI) => {
    try {
      await service.deleteSalary(salaryId);
      return salaryId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Slice
const salarySlice = createSlice({
  name: 'salaries',
  initialState,
  reducers: {
    resetState(state) {
      state.error = null;
      state.successMessage = '';
      state.status = 'idle';
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    removeSalary(state, action) {
      state.salaries = state.salaries.filter(salary => salary._id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Create salary
      .addCase(createSalary.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createSalary.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.salaries.push(action.payload);
        state.successMessage = 'Salary created successfully!';
      })
      .addCase(createSalary.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Fetch all salaries with pagination and search
      .addCase(fetchSalaries.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSalaries.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.salaries = action.payload.salaries;
        state.totalPages = action.payload.totalPages;
        state.totalSalaries = action.payload.totalSalaries;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchSalaries.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Fetch all salaries
      .addCase(fetchAllSalaries.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAllSalaries.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.salaries = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.salaries || [];
      })
      .addCase(fetchAllSalaries.rejected, (state, action) => {
        state.status = 'failed';
        console.error('fetchAllSalaries rejected error:', action.payload);
        state.error = action.payload?.message || 'Failed to fetch salaries';
      })

      // Fetch single salary by ID
      .addCase(fetchSalaryById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSalaryById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.salary = action.payload;
      })
      .addCase(fetchSalaryById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Update salary
      .addCase(updateSalary.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSalary.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.salaries.findIndex((s) => s._id === action.payload._id);
        if (index !== -1) {
          state.salaries[index] = action.payload;
        }
        state.successMessage = 'Salary updated successfully!';
      })
      .addCase(updateSalary.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Delete salary
      .addCase(deleteSalary.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteSalary.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.salaries = state.salaries.filter((s) => s._id !== action.payload);
        state.successMessage = 'Salary deleted successfully!';
      })
      .addCase(deleteSalary.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { resetState, setSearchQuery, setCurrentPage } = salarySlice.actions;
export default salarySlice.reducer;
